import { useEffect, useRef } from "react";
import {
  collectionsListName,
  dependenciesListName,
  departmentsListName,
  favoriteFiltersListName,
} from "src/api/config";
import { useFiltersContext } from "src/contexts/FilterContext";
import { useAppDispatch, useAppSelector } from "src/hooks/redux";
import { getItems } from "src/modules/spItems/actions";
import { getItemsRequest } from "src/modules/spItems/api";
import {
  modifiedItemsState,
  selectGetStatus,
  selectItems,
} from "src/modules/spItems/selectors";
import { selectCurrentUser } from "src/modules/users/selector";
import { FiltersStateType } from "src/pages/DashboardPage/acadia/type";
import { isDocFiltersEmpty } from "src/pages/DashboardPage/acadia/utils/docFilters.utils";
import { consumeDocNavIntent, peekDocNavIntent } from "src/utils/docNavigationIntent";

interface UseDocDefaultFiltersOptions {
  /**
   * When true the hook is active. When the value transitions false → true
   * (e.g. a drawer being re-opened) defaultFilterApplied is reset so the
   * priority chain runs again.
   * Defaults to true (always active — suitable for page-level managers).
   */
  trigger?: boolean;
  /** Called with true once the priority chain has finished. */
  onReady?: (ready: boolean) => void;
  /**
   * Called when Priority 1 (default favourite) is applied.
   * Receives the favourite filter ID so the caller can update isFavorite state.
   */
  onFavoriteApplied?: (filterId: string) => void;
}

/**
 * Encapsulates the auto-apply filter priority logic for document filters:
 *   Priority 0 — explicit navigation from MainPage favourites (one-shot sessionStorage intent)
 *   Priority 1 — default favourite filter
 *   Priority 2 — user's collections (isCollectionAdmin / isCollectionEditor)
 *                + optionally their department when a dependency exists
 *   Priority 3 — nothing matched, just mark ready
 *
 * Used by DocFavoriteFiltersManager (trigger always true) and
 * SelectDocuments drawer (trigger = openDrawer).
 */
function useDocDefaultFilters({
  trigger = true,
  onReady,
  onFavoriteApplied,
}: UseDocDefaultFiltersOptions = {}) {
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => selectCurrentUser(state));

  const favoriteFiltersData = useAppSelector((state) => selectItems(state, favoriteFiltersListName));
  const favoriteFiltersGetStatus = useAppSelector((state) => selectGetStatus(state, favoriteFiltersListName));
  const collectionsData = useAppSelector((state) => selectItems(state, collectionsListName));
  const departmentsData = useAppSelector((state) => selectItems(state, departmentsListName));
  const collectionsGetStatus = useAppSelector((state) => selectGetStatus(state, collectionsListName));
  const departmentsGetStatus = useAppSelector((state) => selectGetStatus(state, departmentsListName));

  const { filters, updateFilter } = useFiltersContext();

  const defaultFilterApplied = useRef(false);
  const prevTrigger = useRef(trigger);

  // Load the user's doc favourite filters whenever the user is ready
  useEffect(() => {
    if (user.email && user.id) {
      dispatch(getItems({ listName: favoriteFiltersListName, filters: { Author: user.id, Type: "doc" } }));
    }
  }, [user.email]);

  // Reset the applied flag when trigger goes false → true (e.g. drawer re-opened)
  useEffect(() => {
    if (!prevTrigger.current && trigger) {
      defaultFilterApplied.current = false;
    }
    prevTrigger.current = trigger;
  }, [trigger]);

  // Main priority chain — runs whenever key loading statuses change
  useEffect(() => {
    if (!trigger) return;
    if (defaultFilterApplied.current) return;
    if (!user.id) return;

    // Priority 0: explicit navigation from MainPage favourites (one-shot intent)
    const navIntent = peekDocNavIntent();
    if (navIntent?.type === "folder") {
      consumeDocNavIntent();
      defaultFilterApplied.current = true;
      onReady?.(true);
      return;
    }

    if (navIntent?.type === "filter") {
      if (favoriteFiltersGetStatus === "SUCCESS") {
        const favorite = (favoriteFiltersData as any[]).find(
          (item) => item.ID.toString() === navIntent.favoriteId.toString(),
        );
        if (favorite?.DocumentsFilter) {
          const parsed = JSON.parse(favorite.DocumentsFilter) as FiltersStateType;
          updateFilter(parsed);
        }
      }

      consumeDocNavIntent();
      defaultFilterApplied.current = true;
      onFavoriteApplied?.(navIntent.favoriteId.toString());
      onReady?.(true);
      return;
    }

    if (favoriteFiltersGetStatus !== "SUCCESS") return;
    if (collectionsGetStatus !== "SUCCESS" || departmentsGetStatus !== "SUCCESS") return;

    // Priority 1: default favourite filter
    const defaultFilter = (favoriteFiltersData as any[]).find((item) => item.isDefault);
    if (defaultFilter?.DocumentsFilter) {
      const parsed = JSON.parse(defaultFilter.DocumentsFilter) as FiltersStateType;
      updateFilter(parsed);
      defaultFilterApplied.current = true;
      onFavoriteApplied?.(defaultFilter.ID.toString());
      onReady?.(true);
      return;
    }

    // Filters already non-empty (manually set) → keep them as is
    if (!isDocFiltersEmpty(filters)) {
      defaultFilterApplied.current = true;
      onReady?.(true);
      return;
    }

    // Priority 2: user's collections (isCollectionAdmin / isCollectionEditor)
    // + optionally their department if a dependency exists in dependenciesListName
    const userCollectionIds = new Set<number>([
      ...(Array.isArray(user.isCollectionAdmin) ? user.isCollectionAdmin : []),
      ...(Array.isArray(user.isCollectionEditor) ? user.isCollectionEditor : []),
    ]);
    const matchedCollections = (collectionsData as any[])
      .filter((c) => userCollectionIds.has(c.ID))
      .slice(0, 1)
      .map((c) => ({ id: c.ID, title: c.Title }));

    if (matchedCollections.length > 0) {
      defaultFilterApplied.current = true;

      (async () => {
        let departmentItems: { id: number; title: string }[] = [];

        if (user.department) {
          const matchedDepts = (departmentsData as any[]).filter(
            (d) => d.User_Department && d.User_Department.indexOf(user.department) !== -1,
          );

          if (matchedDepts.length > 0) {
            const collectionIds = matchedCollections.map((c) => c.id);
            const deptIds = matchedDepts.map((d: any) => d.ID);

            const { items: depItems } = await getItemsRequest({
              listName: dependenciesListName,
              filters: { Collection: collectionIds, Department: deptIds },
            });

            if (depItems.length > 0) {
              const depData = modifiedItemsState(depItems, dependenciesListName);
              const validDeptIds = new Set(depData.map((d) => d.Department.ID));

              departmentItems = matchedDepts
                .filter((d: any) => validDeptIds.has(d.ID))
                .map((d: any) => ({ id: d.ID, title: d.Title }));
            }
          }
        }

        updateFilter({
          ...filters,
          collections: matchedCollections,
          ...(departmentItems.length > 0 && { departments: departmentItems }),
        });
        onReady?.(true);
      })();
      return;
    }

    // Priority 3: nothing matched
    defaultFilterApplied.current = true;
    onReady?.(true);
  }, [trigger, favoriteFiltersGetStatus, user.id, collectionsGetStatus, departmentsGetStatus]);
}

export default useDocDefaultFilters;
